<template lang="pug">
.bulk-edit-wrapper
	#bulk-edit.card-base(@click.stop)
		button.button-close(@click="$emit('close')")
			include ../resources/icons/close.pug
		.header
			.header-3 Bulk Edit
			.switch(@click="toggleSwitch")
						input(v-model="useLegacyAction" type="checkbox")
						.slider

		//-Bulk Action
		template(v-if="useLegacyAction")
			.subheader
				span Choose the type of change and the dates below to update multiple dates at once

			.row.change-row
				.row-header Change 
					
				.row-body
					.selectors
						select.add-remove-select(v-model="bulkAction")
							option( :value="'Remove'") Remove 
							option( :value="'Add'") Add 
							option( :value="'Set Total'") Set Total
						input.dog-amount(v-model="dogAmount" type="number" min="1" max="10" )
						select.dog-size(v-model="selectedDogSize" :class="selectedDogSize")
							option(v-for="size in Dogspaces.Sizes" :key="size" :value="size") {{ Dogspaces.SizeTitle[size] }}
							option(:value="'all'") All Kennels
					//- .notes

			.row.dates-row
				.row-header Dates 
				.row-body
					.selectors.date-range
						.start-date-wrapper
							input.start-date(v-model="startDate" type="date")
							include ../resources/icons/calendar_month.svg
						span -
						.end-date-wrapper
							input.end-date(v-model="endDate" type="date")
							include ../resources/icons/calendar_month.svg
					.notes {{ daysBetween }}


			span.is-available(:class="{'available': slotIsAvailable === true, 'not-available': slotIsAvailable === false}") {{ isAvailableText }}
			span(v-if="!kennelStore.defaultKennelMax") Default kennel maxes not set. Please set the maximums on a single day first.
		template(v-else)
			.subheader
				span Choose the type of change and the dates below to update multiple dates at once

			.row.change-row
				.row-header 
				.size-container(v-for="size in Dogspaces.Sizes" :key="size")
					span(:class="size") {{ Dogspaces.SizeTitle[size] }}
					.kennels
						.dogs
							input(v-model="givenCounts[size]" type="text")
							.label Dogs
						.divider /
						.total
							input(v-model="givenMax[size]" type="text")
							.label Total
					.available(v-if="givenMax[size] - givenCounts[size] >= 0") {{ givenMax[size] - givenCounts[size] }} Available
					.available.bad(v-else) Space Unavailable

			.row.dates-row
				.row-header Dates 
				.row-body
					.selectors.date-range
						.start-date-wrapper
							input.start-date(v-model="startDate" type="date")
							include ../resources/icons/calendar_month.svg
						span -
						.end-date-wrapper
							input.end-date(v-model="endDate" type="date")
							include ../resources/icons/calendar_month.svg
					.notes {{ daysBetween }}
			span.is-available(:class="{'available': invalidSizes.length <= 0, 'not-available': invalidSizes.length > 0}") {{ isAvailableTextList }}

		button.book.secondary.outline(:disabled="buttonDisabled" @click="handleBook") Book
		.loader-container(v-if="loading")
			Loader
</template>
<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import Loader from "./loader/loader.vue";
import moment from "moment";
import { Dogspaces, Kennels } from "@busy-human/k9-library";
import { useKennelCount } from "@/store/kennelCounts";
import { events } from "@/events";

type Props = {
	dogType: Dogspaces.Sizes
	fromDate?: Date
}
const props = defineProps<Props>();
const emit = defineEmits<{ (e: 'close'): void }>();

const kennelStore = useKennelCount();

const loading = ref(false);
const useLegacyAction = ref(false);
const toggleSwitch = () => (useLegacyAction.value = !useLegacyAction.value);

const bulkAction = ref<'Add' | 'Remove' | 'Set Total'>('Add');

const selectedDogSize = ref<Dogspaces.Sizes | 'all'>(props.dogType);

watch(() => props.dogType, size => { selectedDogSize.value = size; });

// const dogLabel = computed(() => {
// 	if (!removeDog.value) {
// 		return "Add a Dog";
// 	}
// 	return "Remove a Dog";
// });

const startDate = ref("");
const endDate = ref("");

async function handleBook() {
	if(useLegacyAction.value){
		await handleBookAction();
	}else{
		await handleBookList();
	}
}

const buttonDisabled = computed(() => {
	if(useLegacyAction.value){
		return !slotIsAvailable.value || !kennelStore.defaultKennelMax;
	}else{
		return invalidSizes.value.length > 0;
	}
});

//Bulk List Functions
const givenCounts = ref(Kennels.Collection.defaults().kennelCount);
const givenMax = ref(Kennels.Collection.defaults().kennelCount);

async function handleBookList(){
	loading.value = true;
	await kennelStore.adjustKennelCountsBulk(startDate.value, endDate.value, givenCounts.value, givenMax.value);
	loading.value = false;
}

const invalidSizes = computed(() => {
	const invalid: string[] = [];
	const sizes = Object.values(Dogspaces.Sizes);
	for(const size of sizes){
		if(givenMax.value[size] - givenCounts.value[size] < 0){
			invalid.push(size);
		}
	}

	return invalid;
});

const isAvailableTextList = computed(() => {
	if(invalidSizes.value.length > 0){
		return `Space Unavailable for ${invalidSizes.value[0]}`;
	}else{
		return "Space Available";
	}
});

//Bulk Action Functions
const dogAmount = ref(1);

async function handleBookAction(){
	console.log(startDate.value, endDate.value);
	loading.value = true;
	if(bulkAction.value === 'Set Total'){
		await kennelStore.setMax(startDate.value, endDate.value, selectedDogSize.value, dogAmount.value);
	}else{
		await kennelStore.addKennel(startDate.value, endDate.value, selectedDogSize.value, bulkAction.value === 'Remove' ? -dogAmount.value : dogAmount.value);
	}
	events.emit("kennel:reload");
	loading.value = false;
	
	emit('close');
}

const slotIsAvailable = ref(false);
const firstUnavailable = ref("");
const sizeUnavailable = ref<Dogspaces.Sizes | null>(null);
watch(() => [startDate.value, endDate.value, selectedDogSize.value, bulkAction.value, dogAmount.value], async () => {
	if(bulkAction.value !== 'Add') {
		slotIsAvailable.value = true;
		return;
	}

	const sizes = selectedDogSize.value === 'all' ? Object.values(Dogspaces.Sizes) : [selectedDogSize.value];
	for(const size of sizes){
		const check = await kennelStore.kennelSpaceAvailable(startDate.value, endDate.value, size, dogAmount.value);

		if(check.avail) {
			slotIsAvailable.value = true;
			firstUnavailable.value = "";
			sizeUnavailable.value = null;
		} else {
			const firstUnavail = check.results.find(r => !r.avail);
			if(!firstUnavail) throw new Error("What?");
			slotIsAvailable.value = false;
			firstUnavailable.value = moment(firstUnavail.date).format('MM/DD/YY');
			sizeUnavailable.value = size;
			break;
		}
	}
});

const isAvailableText = computed(() => {
	if (slotIsAvailable.value) {
		return "Space Available";
	}
	
	//Swap Endate for a date that is full
	return `${firstUnavailable.value} does not have space available for ${sizeUnavailable.value} kennels`;
});



const daysBetween = computed(() => `${moment(endDate.value).diff(moment(startDate.value), 'days', false)} Day(s) Total`);

onMounted(() => {
	const now = moment(props.fromDate);
	startDate.value = now.format("YYYY-MM-DD");
	endDate.value = now.add(1, 'd').format("YYYY-MM-DD");
});

</script>
<style lang="scss">
.bulk-edit-wrapper {
	position: fixed;
	top: 0;
	left: 0;
	z-index: 16;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	background: rgba(0 0 0 / 19.1%);
}

.bulk-edit-wrapper #bulk-edit {
	position: relative;
	display: block;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-width: 28rem;
	height: fit-content;
	padding-bottom: 2.5rem;
	overflow: hidden;
	outline: 1px solid $color-black;
}

#bulk-edit .header {
	display: flex;
	gap: 1rem;
	align-items: center;
	justify-content: center;
}

#bulk-edit select, #bulk-edit input{
	padding: 0.5rem;
	background-color: #F0F0F0;
	border: none;
	border-radius: 0.25rem;
}

#bulk-edit .dog-size{
	&.small {
		background-color: #99DEFF;
	}

	&.medium {
		background-color: #AC97FF;
	}

	&.large {
		background-color: #E497FF;
	}

	&.x-large {
		background-color: #FF9399;
	}

	&.xx-large {
		background-color: #F3FFA4;
	}
}

#bulk-edit .subheader {
	width: 80%;
	margin-bottom: 10px;
	text-align: center;
}

#bulk-edit .loader-container {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	backdrop-filter: blur(4px);
	background: #FFFA;
}

#bulk-edit .size-container {
	display: grid;
	grid-template-columns: 1fr 3fr 1.5fr;
    gap: 2rem;
	align-items: center;
	margin: 1em 0;
	border-radius: 0.25rem;

	span {
		padding: 0.5rem;
		border-radius: 0.25rem;
	}

	.small {
		background-color: #99DEFF;
	}

	.medium {
		background-color: #AC97FF;
	}

	.large {
		background-color: #E497FF;
	}

	.x-large {
		background-color: #FF9399;
	}

	.xx-large {
		background-color: #F3FFA4;
	}

	.kennels {
		display: flex;

	}

	.dogs,
	.total,
	.divider {
		display: flex;
		flex-wrap: wrap;
		gap: 10px 10px;
		align-items: center;
	}

	.divider {
		padding: 0 1em;
	}

	input {
		width: 5em;
		height: 2em;
		text-align: center;
		background-color: #F0F0F0;
		border: none;
		border-radius: 0.25rem;
	}

	.available{
		color: #33B679;
	}

	.available.bad{
		color: #D50000;
	}
}

#bulk-edit .row {
	width: 95%;
	padding: 1rem;
	border-bottom: 1px solid black;

	.row-header{
		margin-bottom: 0.25rem;
		font-family: "Akshar", sans-serif;
	}

	.row-body{
		display: flex;
		align-items: center;
		justify-content: space-between;

		.selectors{
			display: flex;
			gap: 1rem;
		}
	}
}

#bulk-edit .dog-amount{
	&::-webkit-inner-spin-button,
	&::-webkit-outer-spin-button{
		margin: 0;
		-webkit-appearance: none;
	}

	width: 1.5rem;
	text-align: center;
}

#bulk-edit .date-range {
	display: flex;
	gap: 1rem;
	align-items: center;
	font-size: 24px;
	font-weight: bold;

	.start-date-wrapper,
	.end-date-wrapper {
		display: flex;
		gap: .25rem;
		align-items: center;

		.start-date,
		.end-date {
			position: relative;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 0.25rem 0.5rem ;
			padding-right: 0;
			font-family: Akshar, Abel, sans-serif;
			font-size: 15px;
			font-weight: bold;
			color: black;
			background: #F0F0F0;
			border: none;
			border-radius: 4px;

			/* stylelint-disable-next-line order/properties-order */
			/* stylelint-disable-next-line max-nesting-depth */
			&::-webkit-calendar-picker-indicator {
				position: absolute;
				width: 100%;
				background: transparent;
			}
		}
	}

}

#bulk-edit .is-available {
	margin-top: 30px;
	margin-bottom: 10px;

	&.available{
		color: #33B679;
	}

	&.not-available{
		color: #D50000;
	}
}	

#bulk-edit .days-between {
	padding-top: 2em;
}

#bulk-edit .book {
	width: 120px;
	font-family: Akshar, Abel, sans-serif;
	font-size: 18px;
}

#bulk-edit .switch {
		position: relative;
		display: inline-block;
		width: 60px;
		height: 30px;
		margin-bottom:5px;
		}

		.switch input {
			display: none;
		}

		.slider {
			position: absolute;
			cursor: pointer;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			-webkit-transition: 0.4s;
			border: 2px solid black;
			transition: 0.4s;
			border-radius: 34px;

		}

		.slider:before {
			position: absolute;
			content: "";
			height: 26px;
			width: 26px;
			left: 0px;
			bottom:0px;
			background: black;
			-webkit-transition: 0.4s;
			transition: 0.4s;
			border-radius: 50%;

		}

		input:checked + .slider {
			background: black;
		}


		input:checked + .slider:before {
			background:white;
			-webkit-transform: translateX(30px);
			-ms-transform: translateX(30px);
			transform: translateX(30px);
		}
</style>